import React, { useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Link from 'next/link';

import { ChevronLeft, ChevronRight } from '../Icons';
import CloudflareResizedImage from '../CloudflareResizedImage';

import Styles from './CardCarousel.module.scss';

type Props = {
  disableImageLoader?: boolean;
  images?: Array<string>;
  imageAlt?: string;
  isReferrer?: boolean;
  itemAspectRatio?: number;
  link?: string;
  linkAs?: string;
  openLinkInNewTab?: boolean;
  segmentEvent?: () => void;
};

const CardCarousel: React.FC<Props> = ({
  disableImageLoader = false,
  images = [],
  imageAlt,
  isReferrer,
  itemAspectRatio = 0.65,
  link,
  linkAs,
  segmentEvent,
  openLinkInNewTab,
}) => {
  const [currentImage, setCurrentImage] = useState(images[0]);
  const carouselRef = useRef<HTMLDivElement>(null);

  const navigateImage = (index: number) => {
    if (index === images?.length) {
      setCurrentImage(images[0]);
      return;
    }

    if (index === -1) {
      setCurrentImage(images?.[images.length - 1]);
      return;
    }

    setCurrentImage(images[index]);
  };

  return (
    <div
      className={Styles.cardCarousel}
      ref={carouselRef}
      style={{
        // @ts-ignore
        '--sl-card-aspect-ratio': `${itemAspectRatio * 100}%`,
      }}
    >
      <Link href={link || ''} as={linkAs} shallow>
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions, jsx-a11y/anchor-is-valid */}
        <a
          onClick={segmentEvent}
          target={openLinkInNewTab ? '_blank' : ''}
          referrerPolicy={
            isReferrer ? 'no-referrer-when-downgrade' : 'strict-origin-when-cross-origin'
          }
        >
          <CloudflareResizedImage
            disableImageLoader={disableImageLoader}
            lazyRoot={carouselRef}
            alt={imageAlt || 'Current Card'}
            src={currentImage}
            loading="lazy"
            objectFit="cover"
            objectPosition="center"
            layout="fill"
            style={{
              aspectRatio: `${itemAspectRatio}`,
            }}
            sizes="(min-width: 769px) 35vw, (max-width: 768px) 66.6vw"
          />
        </a>
      </Link>
      {images.length > 1 && (
        <>
          <div className={Styles.cardCarouselPreviousWrap} style={{ left: 0 }}>
            <IconButton
              className={Styles.cardCarouselPrevious}
              type="button"
              onClick={() => navigateImage(images.indexOf(currentImage) - 1)}
            >
              <ChevronLeft />
            </IconButton>
          </div>
          <div className={Styles.cardCarouselNextWrap} style={{ right: 0 }}>
            <IconButton
              className={Styles.cardCarouselNext}
              type="button"
              onClick={() => navigateImage(images.indexOf(currentImage) + 1)}
            >
              <ChevronRight />
            </IconButton>
          </div>
          <div className={Styles.cardCarouselIndicators}>
            {images.map((__, index) => (
              <button
                key={`indicator-button-${index + 1}`}
                className={`${Styles.cardCarouselIndicator} ${
                  images.indexOf(currentImage) === index ? Styles.cardCarouselIndicatorActive : ''
                }`}
                type="button"
                onClick={() => navigateImage(index)}
                aria-label={`image ${index} button`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CardCarousel;
